import React from "react"
import { useTranslation } from "react-i18next"
import { useLocation } from "react-router-dom"
import { cn } from "../lib/utils"
import { LayoutProvider, useSidebar } from "../hooks/use-layout"
import { ThemeProvider, useTheme } from "../hooks/use-theme"
import { ChartsProvider } from "../components/charts"
import { Sidebar } from "./sidebar"
import { Header } from "./header"
import { Sheet, SheetContent } from "../components/ui/sheet"
import { CommandPalette } from "../components/command-palette"
import { publicAssetPath } from "helpers/publicAssetPath"
import { isOperationalKioskRoute } from "helpers/operationalRoutes"
interface AppShellProps {
 children: React.ReactNode
 menuItems: any[]
 logo?: string
 appName?: string
}

function AppShellInner({ children, menuItems, logo, appName }: AppShellProps) {
 const { t, i18n } = useTranslation()
 const isRtl = i18n.dir() === "rtl"
 const { pathname, search } = useLocation()
 const hideFooterOnOperational = isOperationalKioskRoute(pathname, search)
 const { isMobileOpen, setMobileOpen, isCollapsed } = useSidebar()
 const mobileNavSide = i18n.dir() === "rtl"? "right":"left"
const { resolvedTheme, setTheme } = useTheme()

 const isDark = resolvedTheme === "dark"
const toggleTheme = () => {
 setTheme(isDark ? "light":"dark")
 }

 return (
 <div className="min-h-screen bg-background app-shell">
 <Sidebar menuItems={menuItems} logo={logo} appName={appName} />
 <CommandPalette menuItems={menuItems as any} />

 <Sheet modal open={isMobileOpen} onOpenChange={setMobileOpen}>
 <SheetContent
 side={mobileNavSide}
 className="w-sidebar border-border-subtle bg-sidebar p-0 text-sidebar-foreground shadow-modal">
 <Sidebar inDrawer menuItems={menuItems} logo={logo} appName={appName} />
 </SheetContent>
 </Sheet>

 <div
 className={cn(
"app-content flex flex-col min-h-screen transition-all duration-300",
 isRtl
 ? cn("mr-0 md:mr-sidebar", isCollapsed && "md:mr-sidebar-collapsed")
 : cn("ml-0 md:ml-sidebar", isCollapsed && "md:ml-sidebar-collapsed"),
 )}
 >
 <Header onToggleTheme={toggleTheme} isDark={isDark} />
 <main className="flex min-h-0 flex-1 flex-col">
 {/* Single page gutter; flex chain lets full-height routes (e.g. POS) fill without 100dvh overflow */}
 <div className="page-transition page-wrapper flex min-h-0 flex-1 flex-col px-3 py-3 sm:px-4 sm:py-4 lg:px-6 lg:py-6">
 {children}
 </div>
 </main>
 <footer
 className={cn(
"border-t border-border-subtle py-3 px-3 text-center text-xs text-muted-foreground sm:px-4 lg:px-6",
 hideFooterOnOperational &&"hidden")}
 >
 <div className="flex flex-wrap items-center justify-center gap-2">
 <img
 src={publicAssetPath("logo.png")}
 alt="madar" className="h-4 w-auto max-w-[80px] object-contain"/>
 <span className="inline-flex flex-wrap items-center justify-center gap-1">
 <span>{t("Developed by")}</span>
 <span dir="ltr" className="font-medium text-foreground">madar</span>
 <span dir="ltr">· © {new Date().getFullYear()}</span>
 </span>
 </div>
 </footer>
 </div>
 </div>
 )
}

export function AppShell({ children, menuItems, logo, appName }: AppShellProps) {
 return (
 <ThemeProvider>
 <ChartsProvider>
 <LayoutProvider>
 <AppShellInner menuItems={menuItems} logo={logo} appName={appName}>
 {children}
 </AppShellInner>
 </LayoutProvider>
 </ChartsProvider>
 </ThemeProvider>
 )
}
